import { useEffect, useRef, useState } from 'react';

import { useLocation } from 'js/lib/useRouter';

import { useCoachEpicEnabledState } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { getActiveConversationId, getChatTargetingRule } from 'bundles/common/utils/chatLoaderUtils';
import type { ChatTargetingRule } from 'bundles/common/utils/chatLoaderUtils';
import type { BoostChatConfig } from 'bundles/composable-page-header/hooks/useBoostChatConfig';
import useBoostChatConfig from 'bundles/composable-page-header/hooks/useBoostChatConfig';

type ChatWidgetName = 'BoostChat' | 'CourseraCoach' | undefined;

export type ChatWidgetSelection = {
  chatWidgetToRender: ChatWidgetName;
  boostChatConfig: BoostChatConfig;
};

const useShouldShowBoostChat = (enableChat: boolean, chatConfig: BoostChatConfig['chatConfig']) => {
  // useLocation() subscribes to route changes so currentUrl is recomputed on navigation
  useLocation();
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [targetingRule, setTargetingRule] = useState<ChatTargetingRule | undefined>(undefined);
  useEffect(() => {
    // Effect code is not run in SSR, which is what we want.  Setting the following using state (rather than a local variables) causes a 2nd render pass in CSR and avoids SSR<>CSR mismatch.
    setConversationId(getActiveConversationId());
    // The header help dropdown is always shown (HOME-4951), so chat targeting rules are always skipped.
    setTargetingRule(getChatTargetingRule(currentUrl, enableChat, chatConfig, true));
  }, [currentUrl, enableChat, chatConfig]);

  const showBoostChatButton = !!conversationId || !!targetingRule; // If there is an active chat conversation we always show the chat button on all pages because we want the learner to be able to easily resume that conversation.
  return showBoostChatButton;
};

/**
 * Custom event names used to suppress/restore the chat widget from other parts of the app
 * (e.g., during voice-to-voice role play simulations where the chat button would interfere).
 */
export const CHAT_WIDGET_SUPPRESSED_EVENT = 'chatWidgetSuppressed';
export const CHAT_WIDGET_RESTORED_EVENT = 'chatWidgetRestored';

export const useChatWidgetSelector = (
  courseId?: string,
  { renderBoostChatOnSsr = true }: { renderBoostChatOnSsr?: boolean } = {}
): ChatWidgetSelection => {
  const boostChatConfig = useBoostChatConfig({ renderBoostChatOnSsr });
  const { enableChat, chatConfig, loading: tacoLoading } = boostChatConfig;

  const { showEmbeddedCoach, isLoading } = useCoachEpicEnabledState({ courseId });
  const shouldShowBoostChat = useShouldShowBoostChat(enableChat, chatConfig);
  const loading = isLoading || tacoLoading;

  const [widgetName, setWidgetName] = useState<ChatWidgetName>(undefined);
  const [isSuppressed, setIsSuppressed] = useState(false);

  useEffect(() => {
    // [COACH-1836] Coach chat can be enabled asynchronously and outside of useCoachEpicEnabledState() checks.
    // So we need to listen for the custom event and set the widget name accordingly.
    const handleCoachChatEnabled = () => {
      setWidgetName('CourseraCoach');
    };

    // custom event dispatched from ExternalCoachLauncher component when Coach chat is enabled.
    document.body.addEventListener('coachChatEnabled', handleCoachChatEnabled);
    return () => {
      document.body.removeEventListener('coachChatEnabled', handleCoachChatEnabled);
    };
  }, []);

  // Listen for suppression events from other parts of the app (e.g., voice-to-voice role play)
  useEffect(() => {
    const handleSuppress = () => setIsSuppressed(true);
    const handleRestore = () => setIsSuppressed(false);

    document.body.addEventListener(CHAT_WIDGET_SUPPRESSED_EVENT, handleSuppress);
    document.body.addEventListener(CHAT_WIDGET_RESTORED_EVENT, handleRestore);
    return () => {
      document.body.removeEventListener(CHAT_WIDGET_SUPPRESSED_EVENT, handleSuppress);
      document.body.removeEventListener(CHAT_WIDGET_RESTORED_EVENT, handleRestore);
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      // These widgets are in priority order.
      if (showEmbeddedCoach) {
        setWidgetName('CourseraCoach');
      } else if (shouldShowBoostChat) {
        setWidgetName('BoostChat');
      }
    }
  }, [shouldShowBoostChat, showEmbeddedCoach, loading]);

  // Track the last returned value to avoid returning undefined during loading states
  const lastValueRef = useRef<ChatWidgetName>(undefined);

  // Update the last value when we have a definitive result
  if (widgetName !== undefined) {
    lastValueRef.current = widgetName;
  }

  // When suppressed, hide all chat widgets (e.g., during voice simulation)
  const chatWidgetToRender = isSuppressed ? undefined : loading ? lastValueRef.current : widgetName;

  // Return the last known value during loading to prevent flickering
  return {
    chatWidgetToRender,
    boostChatConfig,
  };
};

export default useChatWidgetSelector;
